import { createContext, isValidElement, type ReactNode } from 'react'

/**
 * Договор подсказки ⓘ (UI-ACTIONS) между приложением и ui-kit: форма карточки
 * и контекст, через который приложение отдаёт описания из реестра действий.
 * ui-kit store не знает (D-052) — только этот контекст.
 */

/** Что изменит нажатие — та же форма, что `ActionEffect` client-runtime. */
export type ActionInfoEffect = 'view' | 'device' | 'read' | 'read-external' | 'write-local' | 'write-external' | 'cash'
/** Как проверено — та же форма, что `ActionStatus` client-runtime. */
export type ActionInfoStatus = 'live' | 'stand-and-test' | 'test-only' | 'unverified' | 'stub' | 'unclear'

export interface ActionInfoEvidenceLine {
  kind: 'unit' | 'server' | 'stand' | 'live'
  title: string
  where: string
}

/** Карточка подсказки. Совместимость с `ActionCard` client-runtime проверяет компилятор в приложении. */
export interface ActionInfoCard {
  id: string
  label: string
  summary: string
  effect: ActionInfoEffect
  effectText: string | null
  access: string | null
  disabledWhen: string | null
  gestureOf: string | null
  status: ActionInfoStatus
  statusReason: string | null
  evidence: readonly ActionInfoEvidenceLine[]
}

export interface ActionInfoSource {
  /** Переключатель «Подсказки» в шапке. */
  enabled: boolean
  describe: (id: string) => ActionInfoCard | undefined
  /** Разработка: элемент с `actionId` без записи помечается «нет описания» (не только цветом). */
  audit: boolean
}

const OFF: ActionInfoSource = { enabled: false, describe: () => undefined, audit: false }

export const ActionInfoContext = createContext<ActionInfoSource>(OFF)

/**
 * «Тихая» область экрана: внутри неё у кнопок и полей НЕ рисуется своя ⓘ — у экрана одна справка сверху слева
 * (`ScreenInfo`). Записи реестра и `actionId` остаются на местах (реестр и линт их по-прежнему требуют),
 * гаснет только значок. Вне области всё работает как раньше.
 */
export const ActionInfoQuietContext = createContext(false)

/**
 * Текст подписи элемента: строки и числа детей. Разметка внутри (значок,
 * `<strong>`) — `undefined`: подпись тогда берётся из `aria-label` или реестра.
 */
export function textOf(node: ReactNode): string | undefined {
  const text = collect(node)
  return text === undefined || text.trim() === '' ? undefined : text.trim()
}

function collect(node: ReactNode): string | undefined {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (Array.isArray(node)) {
    const parts = node.map(collect)
    return parts.every((part) => part !== undefined) ? parts.join('') : undefined
  }
  if (isValidElement(node)) return undefined
  return undefined
}
